import type {
  IconContainerVariant,
  IconFancyContainerProps,
} from '@components/icon-fancy-container/icon-fancy-container';
import type { IconProps } from '@components/icons';
import { IconActive } from '@components/icons/active';
import { IconArchive } from '@components/icons/archive';
import { IconDateExpired } from '@components/icons/date-expired';
import { IconDraft } from '@components/icons/draft';
import { IconWaiting } from '@components/icons/waiting';
import { useScopedTranslate } from '@locale/translate';

import { jobState } from 'helpers/job-lifecycle';
import type { JobListRow } from 'types/jobs';

import { jobsLocale } from './jobs-config';

export type JobStateKey = 'published' | 'scheduled' | 'draft' | 'expired' | 'archived';

/** A scheduled job is a draft with a publish date, which reads as its own state. */
const jobStateKey = (job: JobListRow): JobStateKey => {
  const state = jobState(job);
  if (state === 'draft' && job.dateScheduledPublish) return 'scheduled';
  return state;
};

export const JOB_STATE_ICONS: Record<
  JobStateKey,
  { Icon: (props: IconProps) => JSX.Element; variant: IconContainerVariant }
> = {
  published: { Icon: IconActive, variant: 'blue' },
  scheduled: { Icon: IconWaiting, variant: 'orange' },
  draft: { Icon: IconDraft, variant: 'indigo' },
  expired: { Icon: IconDateExpired, variant: 'purple' },
  archived: { Icon: IconArchive, variant: 'gray' },
};

export const useJobStateIconContainer = (job: JobListRow): IconFancyContainerProps => {
  const { t } = useScopedTranslate(jobsLocale);
  const state = jobStateKey(job);
  const { Icon, variant } = JOB_STATE_ICONS[state];
  const stateLabel = t(`state_${state}`);

  return {
    icon: <Icon />,
    variant,
    hasBorder: true,
    tooltip: stateLabel,
    // The tooltip is pointer-only, so screen readers get the state as text.
    role: 'img',
    'aria-label': stateLabel,
  };
};
